import { defaultControlCenterSection, defaultPlatformSection } from '@/shared/control-center/navigation'
import {
  defaultFinanceSection,
  FINANCE_SECTION_LANDING,
  type FinanceSectionPath,
} from '@/shared/finance/financeNavigation'
import type { MessageId } from '@/shared/i18n/catalogue.en'
import {
  CAPABILITIES,
  FINANCE_AREA_CAPABILITIES,
  LEADS_AREA_CAPABILITIES,
  hasAnyCapability,
  hasCapability,
} from '@/shared/session/capabilities'
import type { Session } from '@/shared/session/types'
import type { IconName } from '@/shared/ui/icons'
import type { NavTone } from '@/shared/workspace/navPanel'
import type { QuickAccessId } from '@/shared/workspace/quickAccessIds'

/**
 * THE WORKSPACE'S PRIMARY DESTINATIONS, DESCRIBED ONCE.
 *
 * Two surfaces render these destinations: the navigation panel draws them as tiles
 * (`app/WorkspaceNav.tsx`) and the quick-access catalogue offers them as pills
 * (`app/quickAccessCatalogue.ts`). Both used to spell out each destination's
 * label, glyph, area colour and capability gate, and nothing compiled
 * differently when the two disagreed: the id set was type-checked, the four
 * facts behind each id were not. A tile whose gate was tightened simply
 * vanished while its pinned pill kept rendering and linking to a route that
 * now answers not found — and the pinning surface is exactly where a wrong
 * gate is invisible.
 *
 * So the facts live HERE, once, and both surfaces read them. It is the same
 * rule the Schedule sections, the Control Center sections, the AI views, the
 * profile sections and the Leads sections already follow — each of those has
 * one table and the catalogue reads it. These were the exception.
 *
 * It sits in `shared/workspace/` rather than beside either reader because both
 * readers are in `app/`, and shell contracts consumed outside the application
 * composition are what this directory is for.
 */

/** The destinations. `Extract` rather than a fresh union: an id spelled wrong here is
 *  a compile error rather than a destination nothing can pin. */
export type WorkspaceDestinationId = Extract<
  QuickAccessId,
  'patients' | 'tasks' | 'processing' | 'leads' | 'finance' | 'control-center' | 'platform' | 'help'
>

/**
 * The addresses these destinations lead to, as literals the router can check.
 * `/leads/referral-partners` is here because the Leads door lands on it for an
 * account that may read partners and not leads; every Finance section for the
 * same reason — the Finance door lands each account on the first section that
 * admits it, so a reader of prices alone lands on the price lists and a
 * period manager on Administration.
 */
export type WorkspaceDestinationPath =
  | '/patients'
  | '/help'
  | '/tasks'
  | '/processing'
  | '/leads'
  | '/leads/referral-partners'
  | FinanceSectionPath
  | '/control-center'
  | '/platform'

/** Where a tile leads for one account, and the bookmark it therefore pins. */
export interface WorkspaceLanding {
  id: QuickAccessId
  to: WorkspaceDestinationPath
}

/**
 * The area colours these destinations span. Narrower than `NavTone` on purpose: the
 * quick-access pill's own tone is a subset of it too, so a destination given a
 * colour the row cannot paint fails to compile here rather than in the pill.
 */
export type WorkspaceDestinationTone = Extract<NavTone, 'clinical' | 'admin'>

export interface WorkspaceDestination {
  id: WorkspaceDestinationId
  /** The destination's NAME. The tile's accessible name is this alone — a
   *  links list reading "Patients, registry, charts and encounters" would bury
   *  the one word a clinician is scanning for. */
  label: MessageId
  /** A phrase that orients, shown on the tile under the name. */
  description: MessageId
  icon: IconName
  tone: WorkspaceDestinationTone
  to: WorkspaceDestinationPath
  /** The gate the destination's OWN route runs, checked here so a link that
   *  leads to a refusal is never drawn. */
  admits: (session: Session | undefined) => boolean
  /**
   * Set only where the tile is a DOOR TO AN AREA rather than a page: the area
   * admits more accounts than its first section does, and the door lands each
   * of them on the section their route resolves. `admits` above still answers
   * for the page itself, which is what the catalogue offers as a pill.
   */
  door?: {
    admits: (session: Session | undefined) => boolean
    landing: (session: Session | undefined) => WorkspaceLanding
  }
}

/**
 * The destinations, in the order the navigation panel draws them. Declared as a
 * `Record` so the id set and the descriptions cannot drift — an id without a
 * description, or a description under an id the catalogue does not know, does
 * not compile — and read as `Object.values` below, where declaration order is
 * the panel's order.
 */
export const WORKSPACE_DESTINATIONS: Record<WorkspaceDestinationId, WorkspaceDestination> = {
  patients: {
    id: 'patients',
    label: 'nav.patients',
    description: 'nav.patientsDescription',
    // Many people: a registry of them. The checked shield it wore is the
    // picture of guarded access, and on the rail it said "security" where
    // the words would have said "Patients"; the shield now stands on Roles.
    icon: 'people',
    tone: 'clinical',
    to: '/patients',
    admits: (session) => hasCapability(session, CAPABILITIES.patientsRead),
  },
  tasks: {
    id: 'tasks',
    label: 'task.title',
    description: 'nav.tasksDescription',
    icon: 'clipboard-check',
    tone: 'clinical',
    to: '/tasks',
    admits: (session) => hasCapability(session, CAPABILITIES.tasksRead),
  },
  processing: {
    id: 'processing',
    label: 'processing.title',
    description: 'processing.tileDescription',
    // A sparkle with a check: work the assistant did that a person confirms.
    // The navigation column draws no glyph for this tile, but the catalogue's
    // pill does, and the plain sparkle already stands for the AI views.
    icon: 'sparkle-check',
    tone: 'clinical',
    to: '/processing',
    // A processing shows a patient, the files it read and the clinical record
    // it proposes, so the list is for an account that may read all three.
    admits: (session) =>
      hasCapability(session, CAPABILITIES.patientsRead) &&
      hasCapability(session, CAPABILITIES.documentsRead) &&
      hasCapability(session, CAPABILITIES.clinicalRecordsRead),
  },
  leads: {
    id: 'leads',
    label: 'nav.leads',
    description: 'nav.leadsDescription',
    // Someone not registered yet, with the plus of registering them: new
    // enquiries and onboarding, which is what the tile says. A bare person
    // is a profile, and stood two tiles under the registry saying nothing.
    icon: 'user-plus',
    tone: 'clinical',
    to: '/leads',
    admits: (session) => hasCapability(session, CAPABILITIES.patientLeadsRead),
    /**
     * THE FIRST DOOR. Leads is an area of two sections, each with
     * its own capability, and the tile stands for an account admitted to
     * either — the same rule `leadsAreaRoute` runs. Where that account cannot
     * read leads themselves the door leads to Referral partners, and PINS
     * that section rather than the area's name: a Leads pill for an account
     * whose `/leads` answers not found is the bookmark this table exists to
     * prevent, and the primary Schedule tile has resolved its section this
     * way since it was written. The area holds exactly these two
     * capabilities, so the branch below needs no third answer.
     */
    door: {
      admits: (session) => hasAnyCapability(session, LEADS_AREA_CAPABILITIES),
      landing: (session) =>
        hasCapability(session, CAPABILITIES.patientLeadsRead)
          ? { id: 'leads', to: '/leads' }
          : { id: 'leads-referral-partners', to: '/leads/referral-partners' },
    },
  },
  finance: {
    id: 'finance',
    label: 'finance.workspace.title',
    description: 'finance.workspace.tileDescription',
    // The desk where money is taken in: accounts, receipts and the monthly
    // close — and, inside it, the price lists that say what care costs.
    icon: 'cash-register',
    tone: 'admin',
    to: '/finance/accounts',
    admits: (session) => hasCapability(session, CAPABILITIES.financeAccountsRead),
    /**
     * THE SECOND DOOR. Finance is an area of scoped sections, and the tile
     * stands for an account admitted to any of them — the same rule
     * `financeAreaRoute` runs. The landing is the section table's own
     * answer, read rather than restated: a prepaid-only reader lands on
     * Prepaid, a reader of prices on the price lists, period and profile
     * readers on Administration. Each pins its admitted section, so no
     * pinned pill leads to a refusal.
     */
    door: {
      admits: (session) => hasAnyCapability(session, FINANCE_AREA_CAPABILITIES),
      landing: (session) => FINANCE_SECTION_LANDING[defaultFinanceSection(session) ?? 'accounts'],
    },
  },
  'control-center': {
    id: 'control-center',
    label: 'nav.controlCenter',
    description: 'nav.controlCenterDescription',
    icon: 'gear',
    tone: 'admin',
    to: '/control-center',
    // The tile's own rule: offered when at least one section is. Personal
    // profile access lives in the signed-in name above the workspace.
    admits: (session) => defaultControlCenterSection(session) !== null,
  },
  platform: {
    id: 'platform', label: 'controlCenter.administration.platform', description: 'controlCenter.administration.platformDescription',
    icon: 'shield-check', tone: 'admin', to: '/platform',
    admits: session => defaultPlatformSection(session) !== null,
  },
  help: {
    id: 'help', label: 'help.title', description: 'help.description', icon: 'question',
    tone: 'clinical', to: '/help', admits: session => !!session,
  },
}

/** The destinations in the panel's order. */
export const WORKSPACE_DESTINATION_LIST: readonly WorkspaceDestination[] =
  Object.values(WORKSPACE_DESTINATIONS)

/**
 * Where this account's tile leads and what it pins, or `null` when the
 * destination is not part of this account and no tile is drawn. Absence means
 * "not part of this account" and never "not built yet".
 */
export function workspaceLanding(
  destination: WorkspaceDestination,
  session: Session | undefined,
): WorkspaceLanding | null {
  const door = destination.door
  if (door) {
    return door.admits(session) ? door.landing(session) : null
  }
  return destination.admits(session) ? { id: destination.id, to: destination.to } : null
}
